import { Injectable } from '@angular/core';

export type SessionEvent =
  | { type: 'USER_ACTIVITY' }
  | { type: 'SESSION_CONTINUE' }
  | { type: 'LOGOUT' };

/**
 * The one `BroadcastChannel('session-control')` of this tab (LEN-2764).
 *
 * A channel never receives its own messages, but another channel object with
 * the same name in the same tab does. Sharing a single object keeps a tab
 * from reacting to the logout it just announced.
 */
@Injectable({ providedIn: 'root' })
export class SessionChannel {
  private readonly channel =
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel('session-control');

  private readonly listeners = new Set<(event: SessionEvent) => void>();

  constructor() {
    if (this.channel) {
      this.channel.onmessage = ({ data }: MessageEvent<SessionEvent>) =>
        this.listeners.forEach(listener => listener(data));
    }
  }

  /** Tells the other tabs. */
  post(event: SessionEvent): void {
    this.channel?.postMessage(event);
  }

  /** Receives what the other tabs post. Returns the unsubscribe function. */
  listen(listener: (event: SessionEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
